import { computed, inject, Injectable, linkedSignal, signal, Signal, WritableSignal } from '@angular/core';

import {
  ClearAllSelectedVehiclesUuid,
  dealerFilterConfigurationMap,
  flattenAggregation,
  getBucketsForAggregatedTerm,
  InventoryManagementBidsFilter,
  InventoryManagementDateRange,
  InventoryManagementDealerFilter,
  InventoryManagementRepostedStatus,
  InventoryManagementSearchResult,
  InventoryManagementState,
  isBucketPresent,
  PaginatedMetadataResponse,
  ResetInventoryManagementDealerFilterConfiguration,
  SearchResultsAggregationBucket,
  StatusFilter,
  UpdateCurrentStatus,
  UpdateInventoryManagementDealerFilterConfiguration,
  UpdateSearchRequest, UserProfileState
} from 'remarketing-angular-library';
import { SearchFilterFactory } from '../../factories/search-filter.factory';
import { select, Store } from '@ngxs/store';
import {
  BidsOffersFilters,
  dealerFilterMap,
  groundedFilterMap,
  GroundedFilters,
  OfferFilters,
  QuestionFilters
} from '../../enums/inventory-management.enum';
import { AccountManagerEmployeeAggregation } from 'src/inventory-management/enums/account-manager.enum';
import {
  filterParameters,
  InventoryManagementFilterConfiguration
} from '../../interfaces/inventory-management-status-configuration.interface';
import { pick } from 'lodash';
import { hasSameValues } from '../../util/util';
import { SaService } from 'smartauction-frontend-micro-library';
import { InventoryManagementSearchResponseMetadata } from 'remarketing-angular-library/models/inventory-management/inventory-management-search-response.model';
import { AccountManager, AccountManagerSharedVehicle, SharedVehicleDealer } from 'src/inventory-management/interfaces/dealer-group.interface';

type CurrentSearchResponse = NoInfer<{
  searchResponse: PaginatedMetadataResponse<InventoryManagementSearchResult, InventoryManagementSearchResponseMetadata>
}>;

type PreviousSearchResponse = {
  source: NoInfer<{
    searchResponse: PaginatedMetadataResponse<InventoryManagementSearchResult, InventoryManagementSearchResponseMetadata>
  }>
  value: NoInfer<SearchResultsAggregationBucket[]>
};

// All Filters Impacted by Side Rail and Utility Bar
@Injectable({ providedIn: 'root' })
export class SearchFilterService extends SaService {
  private readonly inventoryManagementSearchRequest$ = select(InventoryManagementState.searchRequest);
  private readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);
  private readonly inventoryManagementSearchResponse$ = select(InventoryManagementState.searchResponse);
  private readonly inventoryManagementAggregatedResults$ = select(InventoryManagementState.aggregatedResults);
  private readonly inventoryManagementCurrentStatus$ = select(InventoryManagementState.currentStatus);
  private readonly inventoryManagementDealerFilterConfiguration$ = select(
    InventoryManagementState.dealerFilterConfiguration
  );
  private readonly searchFilterFactory: SearchFilterFactory;
  private readonly store: Store;

  private readonly inventoryManagementFilterConfig$ = computed(() => {
    return pick(this.inventoryManagementSearchCriteria$(), filterParameters) as InventoryManagementFilterConfiguration;
  });

  private readonly isSharedListFilterSelected$ = computed(() => {
    return this.inventoryManagementCurrentStatus$() === StatusFilter.SHARED_VEHICLES;
  });

  // the only time this signal should update is when the select all button is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly offerListCreators$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const isNull = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.offerListCreators === null;
      return this.getComputedDealerListResult(current, previous, isNull, 'offers.offerListCreatorUuid');
    }
  });

  // the only time this signal should update is when the select all button is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly offerDealerLists$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const isNull = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.dealersMadeOffers === null;
      return this.getComputedDealerListResult(current, previous, isNull, 'offers.buyerOeUuid');
    }
  });

  // the only time this signal should update is when the 'All' selection is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly sellerCounterOfferDealerLists$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const isNull = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.dealersWithCounterOffers === null;
      return this.getComputedDealerListResult(current, previous, isNull, 'buyersWithCounterOffers');
    }
  });

  // the only time this signal should update is when the select all button is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly questionDealerLists$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current, previous) => {
      const isNull = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.dealersAskedQuestions === null;
      return this.getComputedDealerListResult(current, previous, isNull, 'questionAskedByUuids');
    }
  });

  // the only time this signal should update is when the select all button is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly winningBidsOffersDealerLists$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const isNull = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.dealersWonVehicles === null;
      return this.getComputedDealerListResult(current, previous, isNull, 'buyer');
    }
  });

  // the only time this signal should update is when the select all button is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly losingBidsOffersDealerLists$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const isNull = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.dealersMadeOffersBids === null;
      return this.getComputedDealerListResult(current, previous, isNull, 'losingBidsOrOffersOeUuids');
    }
  });

  // the only time this signal should update is when the 'All' selection is checked.
  // this is to ensure the aggregated users don't change when filtering down.
  private readonly reservedByUsers$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const reservedByUsers = this.store.selectSnapshot(InventoryManagementState.searchCriteria).reservedByUsers;
      const currentAggregatedResults = current.searchResponse?.metadata?.aggregatedResults;
      const previousAggregatedResults = previous?.source?.searchResponse?.metadata?.aggregatedResults;

      if (reservedByUsers.length === 0 && currentAggregatedResults) {
        return getBucketsForAggregatedTerm(currentAggregatedResults, 'reservation.reservedByUserName');
      } else if (previousAggregatedResults) {
        // reset the current node to refer to the previous node
        current.searchResponse = previous?.source?.searchResponse;
        return getBucketsForAggregatedTerm(previousAggregatedResults, 'reservation.reservedByUserName');
      } else {
        return [];
      }
    }
  });

  // the only time this signal should update is when the 'All' selection is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly reservedForDealers$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current: CurrentSearchResponse, previous: PreviousSearchResponse) => {
      const reservedForDealers = this.store.selectSnapshot(InventoryManagementState.searchCriteria).reservedForDealers;
      const currentAggregatedResults = current.searchResponse?.metadata?.aggregatedResults;
      const previousAggregatedResults = previous?.source?.searchResponse?.metadata?.aggregatedResults;

      if (reservedForDealers.length === 0 && currentAggregatedResults) {
        return getBucketsForAggregatedTerm(currentAggregatedResults, 'reservation.reservedForDealerName');
      } else if (previousAggregatedResults) {
        // reset the current node to refer to the previous node
        current.searchResponse = previous?.source?.searchResponse;
        return getBucketsForAggregatedTerm(previousAggregatedResults, 'reservation.reservedForDealerName');
      } else {
        return [];
      }
    }
  });

  // the only time this signal should update is when the 'All' button is checked.
  // this is to ensure the aggregated tenants don't change when filtering down.
  private readonly tenantList$: WritableSignal<SearchResultsAggregationBucket[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current, previous) => {
      const tenantFilterSelected = this.store.selectSnapshot(InventoryManagementState.searchCriteria).tenantUuid;
      const currentAggregatedResults = current.searchResponse?.metadata?.aggregatedResults;
      const previousAggregatedResults = previous?.source?.searchResponse?.metadata?.aggregatedResults;

      // only when the tenant filter radio button is 'ALL' (sent as undefined in the req payload), can we recalculate the tenant values.
      // this is because a user may have filtered by a tenant and then choose an additional filter after
      // this could cause its current filter to disappear from the latest search response aggregation.
      if (!tenantFilterSelected && currentAggregatedResults) {
        return getBucketsForAggregatedTerm(currentAggregatedResults, 'auctionTenant');
      } else if (previousAggregatedResults) {
        // reset the current node to refer to the previous node
        current.searchResponse = previous?.source?.searchResponse;
        return getBucketsForAggregatedTerm(previousAggregatedResults, 'auctionTenant');
      } else {
        return [];
      }
    }
  });

  // the only time this signal should update is when the 'All Recipients' button is checked.
  // this is to ensure the aggregated dealers don't change when filtering down.
  private readonly offerLists$: WritableSignal<SharedVehicleDealer[]> = linkedSignal({
    source: () => ({
      searchResponse: this.inventoryManagementSearchResponse$()
    }),
    computation: (current, previous) => {
      const allRecipientsSelected = this.store.selectSnapshot(InventoryManagementState.searchCriteria).offerLists.length === 0;
      const currentAggregatedResults = current.searchResponse?.metadata?.aggregatedResults;
      const previousAggregatedResults = previous?.source?.searchResponse?.metadata?.aggregatedResults;

      if (allRecipientsSelected && currentAggregatedResults) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, 'offerLists.buyerOeUuid'));
      } else if (previousAggregatedResults) {
        // reset the current node to refer to the previous node
        current.searchResponse = previous?.source?.searchResponse;
        return flattenAggregation(getBucketsForAggregatedTerm(previousAggregatedResults, 'offerLists.buyerOeUuid'));
      } else {
        return [];
      }
    }
  });

  /**
   * Generates the accountManagerEmployees object from the search response metadata
   *
   * @returns linkedSignal:
   *    1. If the aggregated term is missing from the metadata
   *          return an empty set (**this is so when we deselect the filter that doesn't use accountManagerEmployees, the link in the signal gets reset.
   *    2. If the aggregated term is present AND we're NOT filtering on any specific buying dealers
   *          return the flattened aggregated object
   *    3. If we are filtering on specific buying dealers
   *          return the previous value generated by the signal (**this should ALWAYS be the return outcome of #2)
   */
  private readonly accountManagerEmployees$: WritableSignal<AccountManager[]> = linkedSignal({
    source: () => ({
      aggregatedResults: this.inventoryManagementAggregatedResults$()
    }),
    computation: (current, previous) => {
      const currentAggregatedResults = current.aggregatedResults;
      const filterByEmployeeDealerMap = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployeeDealerMap;
      const dealerMapValues = filterByEmployeeDealerMap ? Object.values(filterByEmployeeDealerMap) : undefined;
      if (!isBucketPresent(current.aggregatedResults, AccountManagerEmployeeAggregation.OFFERS)) {
        return [];
      } else if (dealerMapValues && !dealerMapValues.some(value => value !== null && value !== undefined)) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, AccountManagerEmployeeAggregation.OFFERS));
      } else {
        return previous?.value;
      }
    }
  });

  /**
   * Generates the accountManagerEmployeesSoldWon object from the search response metadata
   *
   * @returns linkedSignal:
   *    1. If the aggregated term is missing from the metadata
   *          return the previous value generated by the signal (**this should ALWAYS be the return outcome of #2)
   *    2. If the aggregated term is present AND we're NOT filtering on any specific buying dealers
   *          return the flattened aggregated object
   *    3. If we are filtering on specific buying dealers
   *          return the previous value generated by the signal (**this should ALWAYS be the return outcome of #2)
   */
  private readonly accountManagerEmployeesSoldWon$: WritableSignal<AccountManager[]> = linkedSignal({
    source: () => ({
      aggregatedResults: this.inventoryManagementAggregatedResults$()
    }),
    computation: (current, previous) => {
      const currentAggregatedResults = current.aggregatedResults;
      const filterByEmployeeDealerMap = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployeeDealerMap;
      const dealerMapValues = filterByEmployeeDealerMap ? Object.values(filterByEmployeeDealerMap) : undefined;
      if (!dealerMapValues.some(value => value !== null)) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, AccountManagerEmployeeAggregation.WINNING_BIDS_OFFERS));
      } else {
        return previous?.value;
      }
    }
  });

  /**
   * Generates the accountManagerEmployeesSoldLost object from the search response metadata
   *
   * @returns linkedSignal:
   *    1. If the aggregated term is missing from the metadata
   *          return an empty set (**this is so when we deselect the filter that doesn't use accountManagerEmployeesSoldLost, the link in the signal gets reset.
   *    2. If the aggregated term is present AND we're NOT filtering on any specific buying dealers
   *          return the flattened aggregated object
   *    3. If we are filtering on specific buying dealers
   *          return the previous value generated by the signal (**this should ALWAYS be the return outcome of #2)
   */
  private readonly accountManagerEmployeesSoldLost$: WritableSignal<AccountManager[]> = linkedSignal({
    source: () => ({
      aggregatedResults: this.inventoryManagementAggregatedResults$()
    }),
    computation: (current, previous) => {
      const currentAggregatedResults = current.aggregatedResults;
      const filterByEmployeeDealerMap = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployeeDealerMap;
      const dealerMapValues = filterByEmployeeDealerMap ? Object.values(filterByEmployeeDealerMap) : undefined;
      if (dealerMapValues && !dealerMapValues.some(value => value !== null)) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, AccountManagerEmployeeAggregation.LOSING_BIDS_OFFERS));
      } else {
        return previous?.value;
      }
    }
  });

  /**
   * Generates the accountManagerEmployeesActiveBids object from the search response metadata
   *
   * @returns linkedSignal:
   *    1. If the aggregated term is missing from the metadata
   *          return an empty set (**this is so when we deselect the filter that doesn't use accountManagerEmployeesActiveBids, the link in the signal gets reset.
   *    2. If the aggregated term is present AND we're NOT filtering on any specific buying dealers
   *          return the flattened aggregated object
   *    3. If we are filtering on specific buying dealers
   *          return the previous value generated by the signal (**this should ALWAYS be the return outcome of #2)
   */
  private readonly accountManagerEmployeesActiveBids$: WritableSignal<AccountManager[]> = linkedSignal({
    source: () => ({
      aggregatedResults: this.inventoryManagementAggregatedResults$()
    }),
    computation: (current, previous) => {
      const currentAggregatedResults = current.aggregatedResults;
      const filterByEmployeeDealerMap = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployeeDealerMap;
      const dealerMapValues = filterByEmployeeDealerMap ? Object.values(filterByEmployeeDealerMap) : undefined;
      if (!isBucketPresent(current.aggregatedResults, AccountManagerEmployeeAggregation.ACTIVE_BIDS)) {
        return [];
      } else if (dealerMapValues && !dealerMapValues.some(value => value !== null && value !== undefined)) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, AccountManagerEmployeeAggregation.ACTIVE_BIDS));
      } else {
        return previous?.value;
      }
    }
  });

    /**
   * Generates the accountManagerEmployeesActiveBids object from the search response metadata
   *
   * @returns linkedSignal:
   *    1. If the aggregated term is missing from the metadata
   *          return an empty set (**this is so when we deselect the filter that doesn't use accountManagerEmployeesActiveBids, the link in the signal gets reset.
   *    2. If the aggregated term is present AND we're NOT filtering on any specific buying dealers
   *          return the flattened aggregated object
   *    3. If we are filtering on specific buying dealers
   *          return the previous value generated by the signal (**this should ALWAYS be the return outcome of #2)
   */
  private readonly accountManagerEmployeesSharedVehicles$: WritableSignal<AccountManagerSharedVehicle[]> = linkedSignal({
    source: () => ({
      aggregatedResults: this.inventoryManagementAggregatedResults$()
    }),
    computation: (current, previous) => {
      const currentAggregatedResults = current.aggregatedResults;
      const filterByEmployee = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployee;
      const offerLists = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.offerLists;
      if (!isBucketPresent(current.aggregatedResults, AccountManagerEmployeeAggregation.OFFER_LISTS)) {
        return [];
      } else if (offerLists && offerLists.length === 0 && filterByEmployee && filterByEmployee.length > 0) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, AccountManagerEmployeeAggregation.OFFER_LISTS));
      } else {
        return previous?.value;
      }
    }
  });

    private readonly accountManagerEmployeesOfferLists$: WritableSignal<AccountManagerSharedVehicle[]> = linkedSignal({
    source: () => ({
      aggregatedResults: this.inventoryManagementAggregatedResults$()
    }),
    computation: (current, previous) => {
      const currentAggregatedResults = current.aggregatedResults;
      const filterByEmployeeDealerMap = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployeeDealerMap;
      const dealerMapValues = filterByEmployeeDealerMap ? Object.values(filterByEmployeeDealerMap) : undefined;
      if (!isBucketPresent(current.aggregatedResults, AccountManagerEmployeeAggregation.OFFER_LISTS)) {
        return [];
      } else if (dealerMapValues && !dealerMapValues.some(value => value !== null && value !== undefined)) {
        return flattenAggregation(getBucketsForAggregatedTerm(currentAggregatedResults, AccountManagerEmployeeAggregation.OFFER_LISTS));
      } else {
        return previous?.value;
      }
    }
  });

  constructor() {
    super();
    this.searchFilterFactory = inject(SearchFilterFactory);
    this.store = inject(Store);
  }

  private getComputedDealerListResult(
    current: CurrentSearchResponse,
    previous: PreviousSearchResponse,
    isNull: boolean,
    term: string
  ) {
    const currentAggregatedResults = current.searchResponse?.metadata?.aggregatedResults;
    const previousAggregatedResults = previous?.source?.searchResponse?.metadata?.aggregatedResults;

    if (currentAggregatedResults && isNull) {
      // recalculate
      return getBucketsForAggregatedTerm(currentAggregatedResults, term);
    } else if (previousAggregatedResults) {
      // reset the current node to refer to the previous node
      // It's noteworthy here that current and previous are objects passed in byReference
      // This means that these will get modified in the parent object as well
      current.searchResponse = previous?.source?.searchResponse;
      return getBucketsForAggregatedTerm(previousAggregatedResults, term);
    } else {
      return [];
    }
  }

  private updateSearch(statusProperties: InventoryManagementFilterConfiguration): void {
    this.store.dispatch(
      new UpdateSearchRequest({
        ...this.inventoryManagementSearchRequest$(),
        page: 0,
        searchCriteria: {
          ...this.inventoryManagementSearchCriteria$(),
          ...statusProperties
        }
      })
    );
  }

  get getTenantList(): SearchResultsAggregationBucket[] {
    return this.tenantList$();
  }

  get getOfferLists(): Signal<SharedVehicleDealer[]> {
    return this.offerLists$;
  }

  get getReservedByUsers(): SearchResultsAggregationBucket[] {
    return this.reservedByUsers$();
  }

  get getReservedForDealers(): SearchResultsAggregationBucket[] {
    return this.reservedForDealers$();
  }

  get getOfferListCreators(): SearchResultsAggregationBucket[] {
    return this.offerListCreators$();
  }

  // for use with the Shared Vehicles Section of the left rail
  get getAccountManagerEmployeesForOfferLists(): Signal<AccountManagerSharedVehicle[]> {
    return this.accountManagerEmployeesSharedVehicles$;
  }

  getAccountManagerEmployees(employeeAggregation: AccountManagerEmployeeAggregation): Signal<AccountManager[]> {
    const accountManagerEmployeeMap = {
      [AccountManagerEmployeeAggregation.OFFERS]: this.accountManagerEmployees$ as Signal<AccountManager[]>,
      [AccountManagerEmployeeAggregation.WINNING_BIDS_OFFERS]: this.accountManagerEmployeesSoldWon$ as Signal<AccountManager[]>,
      [AccountManagerEmployeeAggregation.LOSING_BIDS_OFFERS]: this.accountManagerEmployeesSoldLost$ as Signal<AccountManager[]>,
      [AccountManagerEmployeeAggregation.ACTIVE_BIDS]: this.accountManagerEmployeesActiveBids$ as Signal<AccountManager[]>,
      [AccountManagerEmployeeAggregation.OFFER_LISTS]: this.accountManagerEmployeesOfferLists$ as Signal<AccountManager[]>
    }

    if (employeeAggregation in accountManagerEmployeeMap) {
      return accountManagerEmployeeMap[employeeAggregation];
    }
    return undefined;
  }

  getDealerList(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {

    const dealerListMap = {
      [OfferFilters.OFFERS_BY_DEALER]: this.isSharedListFilterSelected$() ? this.offerListCreators$() : this.offerDealerLists$(),
      [QuestionFilters.QUESTIONS_BY_DEALER]: this.questionDealerLists$(),
      [BidsOffersFilters.WINNING_BIDS_OFFERS]: this.winningBidsOffersDealerLists$(),
      [BidsOffersFilters.LOSING_BIDS_OFFERS]: this.losingBidsOffersDealerLists$(),
      [OfferFilters.DEALERS_WITH_COUNTER_OFFERS]: this.sellerCounterOfferDealerLists$()
    };

    if (dealerFilter in dealerListMap) {
      return dealerListMap[dealerFilter];
    }
    return [];
  }

  setFilterStatus(newStatus: StatusFilter, updateStatus = true): void {
    // reset the previous status and update to new status
    const statusProperties = this.searchFilterFactory.buildDefaultFilter(newStatus, updateStatus);

    // don't update the status field if that status is updated from a vehicle toggle on the ALL_VEHICLES filter
    if (updateStatus) {
      this.store.dispatch(new UpdateCurrentStatus(newStatus));
    }

    // clear the selected vehicles if going to the All Vehicles status.
    if (newStatus === StatusFilter.ALL_VEHICLES) {
      this.store.dispatch(new ClearAllSelectedVehiclesUuid());
    }

    this.updateSearch(statusProperties);
  }

  setDateRange(fromDate: string, toDate: string): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.dateRange = { fromDate, toDate } as InventoryManagementDateRange;

    this.updateSearch(statusProperties);
  }

  setBidsFilter(bidFilter: InventoryManagementBidsFilter): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.filterByBids = bidFilter;
    // reset the employeeDealerMap for account manager selections
    statusProperties.filterByEmployeeDealerMap = this.resetEmployeeDealerMap(this.inventoryManagementSearchCriteria$().filterByEmployee);

    this.updateSearch(statusProperties);
  }

  setRepostedFilter(filterByReposted: InventoryManagementRepostedStatus): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.filterByReposted = filterByReposted;

    this.updateSearch(statusProperties);
  }

  setDealerFilter(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    const filterByDealer = dealerFilterMap[dealerFilter];

    // reset all properties that involve checkbox toggles when modifying this filter
    statusProperties.filterByDealer = filterByDealer;

    statusProperties.dealersAskedQuestions = null;
    statusProperties.dealersMadeOffersBids = null;
    statusProperties.dealersWonVehicles = null;
    statusProperties.dealersMadeOffers = null;
    statusProperties.offerListCreators = null;
    statusProperties.dealersWithCounterOffers = null;
    statusProperties.filterByEmployeeDealerMap = this.resetEmployeeDealerMap(this.inventoryManagementSearchCriteria$().filterByEmployee);

    /*
      offerListCreators uses the same dealer filter type as dealersMadeOffers
      so we use this special case to set that configuration
     */
    const isOfferListCreatorFilter = (filterByDealer === InventoryManagementDealerFilter.OFFERS_BY_DEALER && this.isSharedListFilterSelected$());
    const isValidFilter = filterByDealer in dealerFilterConfigurationMap;

    if (isOfferListCreatorFilter || isValidFilter) {
      if (isOfferListCreatorFilter) {
        const userUuid = this.store.selectSnapshot(UserProfileState.userUuid);
        statusProperties.offerListCreators = [userUuid];
      }
      const toggleFilter = dealerFilterMap[dealerFilter];
      const configurationKey = isOfferListCreatorFilter
        ? 'offerListCreators'
        : dealerFilterConfigurationMap[toggleFilter];

      const dealerFilterConfiguration = { ...this.inventoryManagementDealerFilterConfiguration$()[configurationKey] };

      this.store.dispatch([
        new ResetInventoryManagementDealerFilterConfiguration(),
        new UpdateInventoryManagementDealerFilterConfiguration(configurationKey, { isSelectAllChecked: false, isShowAll: dealerFilterConfiguration.isShowAll })
      ]);
    }

    this.updateSearch(statusProperties);
  }

  setGroundedFilter(groundedFilter: GroundedFilters): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.grounded = groundedFilterMap[groundedFilter];

    this.updateSearch(statusProperties);
  }

  setInService(filterByIsv: boolean, newStatus: StatusFilter) {
    const statusProperties = this.searchFilterFactory.buildDefaultFilter(newStatus);

    this.store.dispatch(new UpdateCurrentStatus(newStatus));
    statusProperties.filterByInService = filterByIsv;

    this.updateSearch(statusProperties);
  }

  setReservedByUsers(reservedByUser: string[]): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.reservedByUsers = reservedByUser;

    this.updateSearch(statusProperties);
  }

  setReservedForDealers(reservedForDealer: string[]): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    // if there's no filter on reserved for dealer, param is null
    statusProperties.reservedForDealers = reservedForDealer;

    this.updateSearch(statusProperties);
  }

  setTenantUuid(tenantUuid: string): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.tenantUuid = tenantUuid;

    this.updateSearch(statusProperties);
  }

  setOfferLists(offerLists: string[]): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.offerLists = offerLists;

    this.updateSearch(statusProperties);
  }

  setDealerList(toggleFilter: InventoryManagementDealerFilter, dealerList: string[]) {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties[dealerFilterConfigurationMap[toggleFilter]] = dealerList;

    this.updateSearch(statusProperties);
  }

  setEmployeeDealerMapFilter(employeeDealerMap: Record<string, string[]>): void {
    const statusProperties = this.inventoryManagementFilterConfig$();

    statusProperties.filterByEmployeeDealerMap = employeeDealerMap;

    this.updateSearch(statusProperties);
  }

  resetEmployeeDealerMap(employees: string[]): Record<string, string[]> {
    if (!employees || employees?.length === 0) {
      return undefined;
    }
    const employeeDealerMap: Record<string, string[]> = {};
    employees.forEach(employee => employeeDealerMap[employee] = null);
    return employeeDealerMap;
  }

  toggleSelectAll(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters): void {
    const statusProperties = this.inventoryManagementFilterConfig$();
    const toggleFilter = dealerFilterMap[dealerFilter];
    const isOfferListCreatorFilter = (dealerFilter === OfferFilters.OFFERS_BY_DEALER && this.isSharedListFilterSelected$());
    const dealerFilterField = isOfferListCreatorFilter ? 'offerListCreators' : dealerFilterConfigurationMap[toggleFilter];

    const dealerFilterConfiguration = { ...this.inventoryManagementDealerFilterConfiguration$()[dealerFilterField] };

    dealerFilterConfiguration.isSelectAllChecked = !dealerFilterConfiguration.isSelectAllChecked;

    // to return any vehicle with a question/offer/losing bid/winning bid, set the dealerFilter to null.
    // if you deselect the select all checkbox, set it as an empty set. Deselecting should return zero results.
    if (dealerFilterConfiguration.isSelectAllChecked) {
      statusProperties[dealerFilterField] = this.getDealerList(dealerFilter).map(dealer => dealer.value);
    } else {
      statusProperties[dealerFilterField] = null;
    }

    this.store.dispatch(new UpdateInventoryManagementDealerFilterConfiguration(dealerFilterField, dealerFilterConfiguration));

    this.updateSearch(statusProperties);
  }

  toggleShowAll(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters): void {
    const toggleFilter = dealerFilterMap[dealerFilter];
    const isOfferListCreatorFilter = (dealerFilter === OfferFilters.OFFERS_BY_DEALER && this.isSharedListFilterSelected$());
    const dealerFilterField = isOfferListCreatorFilter ? 'offerListCreators' : dealerFilterConfigurationMap[toggleFilter];

    const dealerFilterConfiguration = { ...this.inventoryManagementDealerFilterConfiguration$()[dealerFilterField] };

    dealerFilterConfiguration.isShowAll = !dealerFilterConfiguration.isShowAll;

    this.store.dispatch(new UpdateInventoryManagementDealerFilterConfiguration(dealerFilterField, dealerFilterConfiguration));
  }

  toggleDealer(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters, dealerUuid: string) {
    const statusProperties = this.inventoryManagementFilterConfig$();
    const toggleFilter = dealerFilterMap[dealerFilter];
    const isOfferListCreatorFilter = (dealerFilter === OfferFilters.OFFERS_BY_DEALER && this.isSharedListFilterSelected$());
    const dealerFilterField = isOfferListCreatorFilter ? 'offerListCreators' : dealerFilterConfigurationMap[toggleFilter];

    const dealerFilterConfiguration = { ...this.inventoryManagementDealerFilterConfiguration$()[dealerFilterField] };
    const allDealersList = this.getDealerList(dealerFilter).map((dealer: SearchResultsAggregationBucket) => dealer.value);
    let currentDealersList = statusProperties[dealerFilterField];

    // select all should always be checked to true if the dealer filter is null
    if (currentDealersList === null) {
      // set the dealer filter to all dealers except for the one sent as a param.
      currentDealersList = [dealerUuid];
    } else if (currentDealersList.includes(dealerUuid)) {
      // remove dealer from list if it's in the search criteria
      currentDealersList = currentDealersList.filter((item: string) => item !== dealerUuid);
      if (currentDealersList.length === 0) {
        currentDealersList = null;
      }
    } else {
      // add dealer if it's not listed in the search criteria
      currentDealersList = [...currentDealersList, dealerUuid];
    }

    // update select all button to true and reset current dealers if the metadata values are the same as in the search criteria
    if (currentDealersList && hasSameValues(currentDealersList, allDealersList)) {
      dealerFilterConfiguration.isSelectAllChecked = true;
    } else {
      dealerFilterConfiguration.isSelectAllChecked = false;
    }

    // update dealer filter configuration
    this.store.dispatch([new UpdateInventoryManagementDealerFilterConfiguration(dealerFilterField, dealerFilterConfiguration)]);

    statusProperties[dealerFilterField] = currentDealersList;
    this.updateSearch(statusProperties);

  }
}
